import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Plus, Edit2, Trash2, Target, TrendingUp, Clock, CheckCircle, AlertTriangle, ListTodo, X, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  critical: { label: 'P0 - Critical', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10' },
  high: { label: 'P1 - High', color: 'bg-orange-500', textColor: 'text-orange-500', bgLight: 'bg-orange-500/10' },
  medium: { label: 'P2 - Medium', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/10' },
  low: { label: 'P3 - Low', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-500/10' },
  backlog: { label: 'Backlog', color: 'bg-gray-500', textColor: 'text-gray-500', bgLight: 'bg-gray-500/10' }
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-gray-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: TrendingUp },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: 'bg-yellow-500', icon: AlertTriangle }
};

const CATEGORIES = [
  'Integration',
  'Feature',
  'Reporting',
  'Workflow',
  'UI/UX',
  'Performance',
  'Other'
];

const BacklogManagement = () => {
  const { isDark } = useTheme();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open'); // Default to 'open' (not_started, in_progress, on_hold)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'backlog',
    story_points: '',
    category: ''
  });

  useEffect(() => {
    fetchBacklogItems();
    fetchStats();
  }, []);

  const fetchBacklogItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backlog`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to fetch backlog items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backlog-stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      story_points: formData.story_points ? parseInt(formData.story_points) : null
    };

    try {
      if (editingItem) {
        // Update existing item
        const response = await fetch(`${BACKEND_URL}/api/backlog/${editingItem.item_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          fetchBacklogItems();
          fetchStats();
          closeModal();
        }
      } else {
        // Create new item
        const response = await fetch(`${BACKEND_URL}/api/backlog`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          fetchBacklogItems();
          fetchStats();
          closeModal();
        }
      }
    } catch (error) {
      console.error('Failed to save backlog item:', error);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backlog/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchBacklogItems();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this backlog item?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/backlog/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchBacklogItems();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to delete backlog item:', error);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      priority: item.priority,
      story_points: item.story_points?.toString() || '',
      category: item.category || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      priority: 'backlog',
      story_points: '',
      category: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      priority: 'backlog',
      story_points: '',
      category: ''
    });
  };

  // Filter items
  const filteredItems = items.filter(item => {
    if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
    if (filterStatus === 'open') {
      // 'open' includes not_started, in_progress, and on_hold
      if (!['not_started', 'in_progress', 'on_hold'].includes(item.status)) return false;
    } else if (filterStatus !== 'all' && item.status !== filterStatus) {
      return false;
    }
    return true;
  });

  return (
    <AppLayout title="Product Backlog">
      <div className="space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#A2FF00]/10">
                  <ListTodo className="w-5 h-5 text-[#A2FF00]" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Items</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total_items}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Target className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Story Points</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total_story_points}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Completed</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.status_counts?.completed || 0}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>In Progress</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.status_counts?.in_progress || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Add Button */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} border`}
            >
              <option value="all">All Priorities</option>
              <option value="critical">P0 - Critical</option>
              <option value="high">P1 - High</option>
              <option value="medium">P2 - Medium</option>
              <option value="low">P3 - Low</option>
              <option value="backlog">Backlog</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} border`}
            >
              <option value="open">Open (Active)</option>
              <option value="all">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-[#A2FF00] text-black font-semibold rounded-lg hover:bg-[#8FE600] transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>

        {/* Backlog Items List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#A2FF00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No backlog items found</p>
            <button onClick={openCreateModal} className="mt-4 text-[#A2FF00] hover:underline">Add your first item</button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => {
              const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.backlog;
              const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.not_started;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={item.item_id}
                  className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10' : 'bg-white border border-gray-200 hover:shadow-md'} transition-all`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityConfig.bgLight} ${priorityConfig.textColor}`}>
                          {priorityConfig.label}
                        </span>
                        {item.category && (
                          <span className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            {item.category}
                          </span>
                        )}
                        {item.story_points !== null && item.story_points !== undefined && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-[#A2FF00]/10 text-green-700'}`}>
                            {item.story_points} pts
                          </span>
                        )}
                      </div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                      {item.description && (
                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.description}</p>
                      )}
                      <div className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Created by {item.created_by_name} • {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.item_id, e.target.value)}
                        className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border`}
                      >
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on_hold">On Hold</option>
                      </select>
                      <button
                        onClick={() => openEditModal(item)}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.item_id)}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-red-500/20 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-500 hover:text-red-500'} transition-colors`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-xl ${isDark ? 'bg-[#0A0A0A] border border-white/10' : 'bg-white border border-gray-200'} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editingItem ? 'Edit Backlog Item' : 'Add Backlog Item'}
              </h3>
              <button onClick={closeModal} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:outline-none`}
                  placeholder="e.g., Integration to Brevo"
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:outline-none`}
                  rows={3}
                  placeholder="Describe the backlog item..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:outline-none`}
                  >
                    <option value="critical">P0 - Critical</option>
                    <option value="high">P1 - High</option>
                    <option value="medium">P2 - Medium</option>
                    <option value="low">P3 - Low</option>
                    <option value="backlog">Backlog</option>
                  </select>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Story Points</label>
                  <input
                    type="number"
                    value={formData.story_points}
                    onChange={(e) => setFormData({ ...formData, story_points: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:outline-none`}
                    min="0"
                    max="100"
                    placeholder="e.g., 5"
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:outline-none`}
                >
                  <option value="">Select category...</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className={`px-4 py-2 rounded-lg ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#A2FF00] text-black font-semibold rounded-lg hover:bg-[#8FE600] transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default BacklogManagement;
